import WolfCard from '@components/ui/WolfCard';
import { AreaIcon24, ServicesIcon24 } from '@components/ui/WolfIcons';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '@hooks/useTranslation';
import { Area } from '@models/area.model';
import { Process } from '@models/process.model';
import { Service } from '@models/service.model';
import { TriggerType } from '@models/trigger.model';
import { UserRoles } from '@models/user.model';
import { CalendarMonthSharp, ElectricBoltOutlined, GroupWorkOutlined, PolicyOutlined } from '@mui/icons-material';
import { Box, Fade, Grid } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import getCategoriesNames from '@utils/getCategoriesNames';
import { convertDragEndToDropResult, DropResult } from '@utils/notValidDrop';
import { useHistory } from 'react-router-dom';

interface ItemWithSummaryDragProps {
  elements: Service[] | Area[] | Process[];
  isGridView?: boolean;
  isSidebarOpen?: boolean;
  elementIdName: 'serviceId' | 'areaId' | 'processId';
  hasCalendar: boolean;
  hasStorage: boolean;
  showDrag: boolean;
  showEdit?: boolean;
  recentlyDroppedId?: string | null;
  handleDragEnd: (result: DropResult) => void;
  handleTitleClick?: (id: string) => void;
  handleCardClick: (id: string, e?: React.MouseEvent) => void;
  drawerEditOpen: (id: string) => void;
  cardsRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
}

const ItemWithSummaryDrag: React.FC<ItemWithSummaryDragProps> = ({
  elements,
  isGridView = false,
  isSidebarOpen = false,
  hasCalendar,
  hasStorage,
  showDrag,
  elementIdName,
  recentlyDroppedId,
  handleDragEnd,
  handleCardClick,
  drawerEditOpen,
  cardsRefs,
}) => {
  const localeSummaryCard = useTranslation('summaryCard');
  const history = useHistory();
  const { role } = useAppSelector((state: RootState) => state.user);

  const editAction = (e: any, id: string) => {
    e.stopPropagation();
    if (elementIdName === 'processId') {
      const url = `/processes/${id}`;
      if (e.ctrlKey || e.metaKey) {
        window.open(url, '_blank');
      } else {
        history.push(url);
      }
    } else {
      drawerEditOpen(id);
    }
  };

  const _handleBadgeClick = (e: any, badge: string, element: any, elementIdName: string) => {
    e.stopPropagation();
    let url = '';
    if (elementIdName === 'serviceId') {
      const auxId = (element as Service).serviceId;
      url = `/processes?serviceId=${auxId}&category=${badge}`;
    }
    if (elementIdName === 'areaId') {
      const auxId = (element as Area).services?.find((service) => service.name === badge)?.serviceId;
      url = `/processes?serviceId=${auxId}`;
    }

    if (url) {
      if (e.ctrlKey || e.metaKey) {
        window.open(url, '_blank');
      } else {
        history.push(url);
      }
    }
  };

  const handleMapClick = (e: any, element: Service | Area | Process) => {
    e.stopPropagation();
    let url = '';
    if (elementIdName === 'processId') {
      url = `/processMap?processId=${(element as Process).processId}`;
    }
    if (elementIdName === 'serviceId') {
      url = `/processMap?serviceId=${(element as Service).serviceId}`;
    }
    if (elementIdName === 'areaId') {
      url = `/processMap?areaId=${(element as Area).areaId}`;
    }

    // Check if ctrl/cmd key is pressed for opening in new tab
    if (e.ctrlKey || e.metaKey) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const getBadges = (element: Service | Area | Process) => {
    if (elementIdName === 'serviceId') {
      return getCategoriesNames(element as Service);
    } else if (elementIdName === 'processId') {
      return [];
      // return (element as Process).policies?.map((policy) => policy.name) || [];
    } else {
      return (element as Area).services?.map((service) => service.name) || [];
    }
  };

  const getSummaryCounts = (
    element: Service | Area | Process,
  ): { icon: React.ReactNode; tooltip: string; count: number }[] => {
    if (elementIdName === 'processId') {
      return [
        {
          icon: <ElectricBoltOutlined />,
          tooltip: localeSummaryCard['numberEventTriggers'],
          count:
            (element as Process).triggers?.filter((trigger) => trigger.triggerType === TriggerType.EVENT).length || 0,
        },
        {
          icon: <CalendarMonthSharp />,
          tooltip: localeSummaryCard['numberCalendarTriggers'],
          count:
            (element as Process).triggers?.filter((trigger) => trigger.triggerType === TriggerType.CALENDAR).length ||
            0,
        },
        {
          icon: <PolicyOutlined />,
          tooltip: localeSummaryCard['numberPolicies'],
          count: (element as Process).policies?.length || 0,
        },
      ];
    } else {
      return [];
    }
  };

  const getheaderIcon = (element: Service | Area | Process) => {
    if (elementIdName === 'areaId') {
      return <AreaIcon24 />;
    } else if (elementIdName === 'serviceId') {
      return <ServicesIcon24 />;
    } else if (elementIdName === 'processId') {
      return <GroupWorkOutlined />;
    } else {
      return <ServicesIcon24 />;
    }
  };

  const getEmptyMessage = (element: Service | Area | Process) => {
    if (elementIdName === 'processId') {
      if ((element as Process).steps?.length === 0 || !(element as Process).steps) {
        return localeSummaryCard['emptyProcess'];
      } else {
        return '';
      }
    } else if (elementIdName === 'serviceId') {
      if ((element as Service).processes?.length === 0 || !(element as Service).processes) {
        return localeSummaryCard['emptyService'];
      } else {
        return '';
      }
    } else if (elementIdName === 'areaId') {
      if ((element as Area).services?.length === 0 || !(element as Area).services) {
        return localeSummaryCard['emptyArea'];
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEndInternal = (event: DragEndEvent) => {
    const result = convertDragEndToDropResult(event);
    if (result) {
      handleDragEnd(result);
    }
  };

  const SortableCard = ({ element }: { element: any; index: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: element[elementIdName],
      disabled: !showDrag || role === UserRoles.USER,
    });

    const style = {
      opacity: isDragging ? 0.3 : 1,
    };

    const draggableStyle = {
      transform: CSS.Transform.toString(transform),
      transition: transition || 'transform 200ms ease',
      touchAction: 'none',
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isDragging ? 1000 : 'auto',
      transformOrigin: 'center center',
    };

    return (
      <Grid
        key={element[elementIdName]}
        item
        xs={12}
        md={isGridView ? (isSidebarOpen ? 12 : 6) : 12}
        lg={isGridView ? (isSidebarOpen ? 6 : 4) : 12}
        xl={elementIdName === 'areaId' ? (isGridView ? 3 : 12) : isGridView ? 4 : 12}
        style={style}>
        <Fade in={true}>
          <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={draggableStyle}
            className="w-full relative flex justify-center">
            {cardsRefs && (
              <Box
                sx={{ scrollMargin: 100 }}
                ref={(el: HTMLDivElement) => (cardsRefs.current[element[elementIdName]] = el)}></Box>
            )}
            <WolfCard
              key={element[elementIdName]}
              color={element.color || element?.service?.color}
              name={element.name}
              description={elementIdName === 'processId' ? '' : element.description}
              goal={element.goal || ''}
              showCalendar={hasCalendar}
              calendarLink={element.calendar?.id}
              showStorage={hasStorage}
              storageLocation={element.storageLocation}
              showDocument={elementIdName === 'processId' && !!element?.location}
              documentLocation={element?.location}
              badges={getBadges(element)}
              handleBadgeClick={(e: any, badge: string) =>
                _handleBadgeClick(e, badge, element, elementIdName)
              }
              status={element?.status}
              summaryCounts={getSummaryCounts(element)}
              headerIcon={getheaderIcon(element)}
              showEditButton={role !== UserRoles.USER}
              showDragIcon={role !== UserRoles.USER && showDrag}
              isRecentlyDropped={recentlyDroppedId === element[elementIdName]}
              editAction={(e) => editAction(e, element[elementIdName])}
              cardClickAction={(e) => handleCardClick && handleCardClick(element[elementIdName], e)}
              emptyMessage={getEmptyMessage(element)}
              handleMapClick={(e: any) => handleMapClick(e, element)}
            />
          </Box>
        </Fade>
      </Grid>
    );
  };

  const items = elements.map((el: any) => el[elementIdName]);
  const strategy = isGridView ? rectSortingStrategy : verticalListSortingStrategy;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndInternal}>
      <SortableContext items={items} strategy={strategy}>
        <div className="flex flex-wrap justify-center w-full">
          <Grid container spacing={3} alignItems={'stretch'}>
            {elements.map((element: any, index: number) => (
              <SortableCard key={element[elementIdName]} element={element} index={index} />
            ))}
          </Grid>
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ItemWithSummaryDrag;
